
import { useEffect, useRef } from 'react';
import { useUserPreferencesStore } from '@/store/user-preferences.store';
import { useAuth } from '@/store/auth.store';
import { useOrganizationStore } from '@/store/organization.store';
import { createLogger } from '@/lib/logger';

const logger = createLogger('components/preferences/UserPreferencesInitializer');

/**
 * UserPreferencesInitializer loads all user preferences for the current organization
 * as soon as the app initializes. This component should be mounted near the root of the app.
 */
export function UserPreferencesInitializer() {
  const { user } = useAuth();
  const currentOrganization = useOrganizationStore(state => state.currentOrganization);
  const { fetchUserPreferences, clearPreferences, isInitialized, isLoading } = useUserPreferencesStore();

  const prevOrgIdRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    const currentOrgId = currentOrganization?.id || null;
    const previousOrgId = prevOrgIdRef.current;

    // Organization changed - clear and refetch
    if (previousOrgId !== null && previousOrgId !== currentOrgId) {

      // Clear all preferences for the old organization
      clearPreferences();
      isFetchingRef.current = false;

      // If we have a new organization, immediately fetch its preferences
      if (currentOrgId && user?.id) {
        isFetchingRef.current = true;

        fetchUserPreferences()
          .then(() => {
            isFetchingRef.current = false;
          })
          .catch((error) => {
            logger.error('[UserPreferencesInitializer] ❌ Failed to fetch preferences for new org:', error);
            isFetchingRef.current = false;
          });
      }
    }
    // Initial mount or first organization set
    else if (previousOrgId === null && currentOrgId && user?.id && !isInitialized && !isLoading && !isFetchingRef.current) {
      isFetchingRef.current = true;

      fetchUserPreferences()
        .then(() => {
          isFetchingRef.current = false;
        })
        .catch((error) => {
          logger.error('[UserPreferencesInitializer] ❌ Initial preferences fetch failed:', error);
          isFetchingRef.current = false;
        });
    }

    // Update ref for next comparison
    prevOrgIdRef.current = currentOrgId;
  }, [currentOrganization?.id, user?.id, fetchUserPreferences, clearPreferences, isInitialized, isLoading]);

  // This is a utility component that doesn't render anything
  return null;
} 